<template>
	<div class="stats-chat" :class="{ 'is-rtl': isRTL }" v-show="!isScreenVisible">
		<transition name="fab-fade">
			<div v-if="showFab && !hidden" class="chat-fab-wrap" key="fab">
				<button
					class="chat-fab"
					:aria-label="$t('chat_ask_buzzz')"
					@click="openPanel"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
						<path d="M8 9h8M8 13h4" opacity="0.7"/>
					</svg>
					<span class="chat-fab-label">{{ $t('chat_ask_buzzz') }}</span>
				</button>
				<button class="chat-fab-hide" :title="$t('chat_hide')" @click.stop="hidden = true">
					<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
				</button>
			</div>
		</transition>

		<transition name="fab-fade">
			<button v-if="hidden" class="chat-fab-mini" key="mini" :title="$t('chat_ask_buzzz')" @click="hidden = false; openPanel()">
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
				</svg>
			</button>
		</transition>

		<transition name="chat-panel" @after-leave="onPanelClosed">
			<div v-if="open" class="chat-panel" :dir="isRTL ? 'rtl' : 'ltr'">
				<div class="chat-panel-header">
					<div class="chat-header-left">
						<span class="chat-header-avatar">
							<img :src="require('@/assets/buzzztv_logo.svg')" alt="Buzzz" class="chat-avatar-logo" />
						</span>
						<div class="chat-header-info">
							<h3 class="chat-panel-title">Buzzz</h3>
							<span class="chat-header-status" :class="{ thinking: loading }">
								<span class="chat-status-dot"></span>
								{{ headerStatus }}
							</span>
						</div>
					</div>
					<div class="chat-header-actions">
						<button v-if="messages.length" type="button" class="chat-header-btn" :title="$t('chat_export')" @click="exportConversation">
							<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
						</button>
						<button v-if="messages.length" type="button" class="chat-header-btn" :aria-label="$t('chat_clear')" @click="clearChat">
							<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
						</button>
						<button type="button" class="chat-close" aria-label="Close" @click="open = false">
							<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
						</button>
					</div>
				</div>

				<div class="chat-panel-body">
					<div ref="messagesContainer" class="chat-messages">
						<!-- Welcome state -->
						<div v-if="messages.length === 0" class="chat-welcome">
							<div class="chat-welcome-icon">
								<img :src="require('@/assets/buzzztv_logo.svg')" alt="Buzzz" class="chat-avatar-logo chat-avatar-logo--welcome" />
							</div>
							<p class="chat-welcome-greeting" v-if="userName">{{ $t('chat_greeting', { name: userName }) }}</p>
							<p class="chat-welcome-text">{{ chatPlaceholder }}</p>
							<div class="chat-welcome-grid">
								<button
									v-for="(chip, idx) in welcomeChips"
									:key="chip.prompt + '-' + idx"
									type="button"
									class="chat-welcome-card"
									:disabled="loading"
									@click="sendChip(chip)"
								>
									<span class="chat-welcome-card-icon">{{ chip.icon }}</span>
									<span class="chat-welcome-card-label">{{ chip.label }}</span>
									<button
										type="button"
										class="chat-welcome-card-pin"
										:class="{ pinned: isPinned(chip) }"
										:title="isPinned(chip) ? $t('chat_unpin') : $t('chat_pin')"
										@click.stop="togglePin(chip)"
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
									</button>
								</button>
							</div>
							<div class="chat-deep-section">
								<div class="chat-deep-label">
									<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
									<span>{{ $t('chat_deep_analysis') }}</span>
								</div>
								<div class="chat-welcome-grid">
									<button
										v-for="(chip, idx) in deepChips"
										:key="'deep-' + idx"
										type="button"
										class="chat-welcome-card chat-welcome-card-deep"
										:disabled="loading"
										@click="sendChip(chip)"
									>
										<span class="chat-welcome-card-icon">{{ chip.icon }}</span>
										<span class="chat-welcome-card-label">{{ chip.label }}</span>
										<button
											type="button"
											class="chat-welcome-card-pin"
											:class="{ pinned: isPinned(chip) }"
											:title="isPinned(chip) ? $t('chat_unpin') : $t('chat_pin')"
											@click.stop="togglePin(chip)"
										>
											<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
										</button>
									</button>
								</div>
							</div>
						</div>

						<!-- Messages -->
						<div
							v-for="(m, i) in messages"
							:key="i"
							:class="['chat-msg', m.role, { 'chat-msg-error': m.isError }]"
						>
							<span v-if="m.role === 'assistant'" class="chat-msg-avatar">
								<img :src="require('@/assets/buzzztv_logo.svg')" alt="Buzzz" class="chat-avatar-logo chat-avatar-logo--msg" />
							</span>
							<div class="chat-msg-body">
							<div v-if="m.role === 'assistant' && loading && i === messages.length - 1 && !m.content" class="chat-msg-content chat-loading">
								<span class="loading-dots"><span></span><span></span><span></span></span>
								<span v-if="toolStatusText" class="chat-tool-status">{{ toolStatusText }}</span>
							</div>
							<div v-else-if="m.role === 'assistant'" class="chat-msg-content chat-msg-markdown" v-html="renderMarkdown(m.content)"></div>
							<div v-else class="chat-msg-content">{{ m.content }}</div>
							<span v-if="m.ts && !(loading && i === messages.length - 1 && !m.content)" class="chat-msg-time">{{ formatTime(m.ts) }}</span>
						<div v-if="m.role === 'assistant' && m.content && !(loading && i === messages.length - 1 && !m.content)" class="chat-msg-actions">
							<button
								v-if="m.isError"
								type="button"
								class="chat-action-btn chat-retry-btn"
								:title="$t('chat_retry')"
								:disabled="loading"
								@click="retryFromError(i)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
								<span>{{ $t('chat_retry') }}</span>
							</button>
							<button
								type="button"
								class="chat-action-btn chat-copy-btn"
								:title="$t('chat_copy')"
								@click="copyMessage(m)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
								<span>{{ copiedIndex === i ? $t('chat_copied') : $t('chat_copy') }}</span>
							</button>
							<button
								type="button"
								class="chat-action-btn chat-share-btn"
								:title="$t('chat_share')"
								@click="shareMessage(m)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
								<span>{{ $t('chat_share') }}</span>
							</button>
							<span v-if="m.logId && !m.isError" class="chat-feedback-divider"></span>
							<button
								v-if="m.logId && !m.isError && !m.feedbackGiven"
								type="button"
								class="chat-action-btn chat-feedback-btn chat-feedback-up"
								:title="$t('chat_feedback_good')"
								@click="giveFeedback(i, 2)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
							</button>
							<button
								v-if="m.logId && !m.isError && !m.feedbackGiven"
								type="button"
								class="chat-action-btn chat-feedback-btn chat-feedback-down"
								:title="$t('chat_feedback_bad')"
								@click="giveFeedback(i, 1)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
							</button>
							<span v-if="m.feedbackGiven === 'thanks'" class="chat-feedback-thanks">{{ $t('chat_feedback_thanks') }}</span>
						</div>
						<div v-if="m.showReasonTags" class="chat-feedback-tags">
							<button
								v-for="tag in feedbackReasonTags"
								:key="tag.key"
								type="button"
								class="chat-feedback-tag"
								:class="{ selected: (m.feedbackSelectedTags || []).includes(tag.key) }"
								@click="toggleFeedbackTag(i, tag.key)"
							>{{ $t(tag.label) }}</button>
							<button type="button" class="chat-feedback-tag chat-feedback-tag-submit" @click="submitFeedbackTags(i)">{{ $t('chat_feedback_submit') }}</button>
						</div>
						</div><!-- /chat-msg-body -->
						</div>
					</div>

					<!-- Inline chips (only when there are messages) -->
					<div v-if="messages.length && !showChipsInline" class="chat-chips-toggle">
						<button type="button" class="chat-chips-toggle-btn" @click="showChipsInline = true">
							<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
							{{ $t('chat_suggestions') }}
						</button>
					</div>
					<div v-if="messages.length && showChipsInline" class="chat-chips-row">
						<button type="button" class="chat-chips-hide" @click="showChipsInline = false">
							<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
						</button>
						<div class="chat-chips-scroll">
							<button
								v-for="(chip, idx) in dynamicChips"
								:key="'d-' + idx"
								type="button"
								class="chat-chip-pill chat-chip-dynamic"
								:disabled="loading"
								@click="sendChip(chip); showChipsInline = false"
							>{{ chip.icon }} {{ chip.label }}</button>
							<button
								v-for="(chip, idx) in inlineChips"
								:key="'s-' + idx"
								type="button"
								class="chat-chip-pill"
								:disabled="loading"
								@click="sendChip(chip); showChipsInline = false"
							>{{ chip.icon }} {{ chip.label }}</button>
						</div>
					</div>

					<form class="chat-form" @submit.prevent="send">
						<div class="chat-input-wrapper">
							<input
								ref="chatInput"
								v-model="input"
								type="text"
								class="chat-input"
								:placeholder="$t('chat_input_placeholder')"
								:disabled="loading"
								autocomplete="off"
								@input="onInputChange"
								@keydown.down.prevent="selectAutocomplete(1)"
								@keydown.up.prevent="selectAutocomplete(-1)"
								@keydown.enter.prevent="confirmAutocomplete"
								@keydown.escape="autocompleteItems = []"
							/>
							<div v-if="autocompleteItems.length" class="chat-autocomplete">
								<button
									v-for="(item, idx) in autocompleteItems"
									:key="idx"
									type="button"
									class="chat-autocomplete-item"
									:class="{ active: idx === autocompleteIdx }"
									@mousedown.prevent="applyAutocomplete(item)"
								>{{ item }}</button>
							</div>
						</div>
						<button v-if="loading" type="button" class="chat-stop" :title="$t('chat_stop')" @click="stopGeneration">
							<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
						</button>
						<button v-else type="submit" class="chat-send" :disabled="!input.trim()">
							<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
						</button>
					</form>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { sendChatMessageStream, sendChatFeedback } from '../services/chatService'
import { trackEvent } from '../../common/services/gaService'
import { mapGetters } from 'vuex'
import { localeData } from '../../common/services/localeService'
import DOMPurify from 'dompurify'
const markedLib = require('marked')

const markedParser =
	(typeof markedLib?.parse === 'function' && markedLib.parse) ||
	(typeof markedLib?.marked === 'function' && markedLib.marked) ||
	(typeof markedLib?.default?.parse === 'function' && markedLib.default.parse) ||
	(typeof markedLib?.default?.marked === 'function' && markedLib.default.marked) ||
	(typeof markedLib?.default === 'function' && markedLib.default) ||
	(typeof markedLib === 'function' && markedLib) ||
	((text) => text)

if (typeof markedLib?.setOptions === 'function') {
	markedLib.setOptions({ breaks: true, gfm: true })
} else if (typeof markedLib?.marked?.setOptions === 'function') {
	markedLib.marked.setOptions({ breaks: true, gfm: true })
} else if (typeof markedLib?.default?.setOptions === 'function') {
	markedLib.default.setOptions({ breaks: true, gfm: true })
}

const STORAGE_KEY_PREFIX = 'buzztv_chat_'
const STORAGE_KEY_PINNED = 'buzztv_chat_pinned'

const TOOL_STATUS_LABELS = {
	get_overview: 'chat_tool_overview',
	get_room_stats: 'chat_tool_rooms',
	get_hourly_stats: 'chat_tool_hours',
	get_daily_stats: 'chat_tool_days',
	get_realtime_status: 'chat_tool_live',
	get_cleaning_priority: 'chat_tool_priority',
	get_room_detail: 'chat_tool_room_detail',
	get_shift_stats: 'chat_tool_shifts',
	get_staff_status: 'chat_tool_staff',
	get_device_status: 'chat_tool_devices',
}

function getStorageKey(company, site) {
	return `${STORAGE_KEY_PREFIX}${company || 'x'}_${site || 'x'}`
}

function renderSparklineSvg(values) {
	const arr = values.map((v) => Number(v)).filter((n) => !Number.isNaN(n))
	if (arr.length < 2) return ''
	const w = 120
	const h = 28
	const pad = 2
	const max = Math.max(...arr)
	const min = Math.min(...arr)
	const range = max - min || 1
	const xStep = (w - pad * 2) / (arr.length - 1)
	const points = arr
		.map((val, i) => {
			const x = pad + i * xStep
			const y = h - pad - ((val - min) / range) * (h - pad * 2)
			return `${x},${y}`
		})
		.join(' ')
	return `<svg class="chat-inline-sparkline" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg"><polyline fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" points="${points}"/></svg>`
}

function renderBarChartSvg(spec) {
	const pairs = spec.split('|').map((s) => s.trim()).filter(Boolean).slice(0, 8)
	if (!pairs.length) return ''
	const entries = pairs.map((p) => {
		const lastComma = p.lastIndexOf(',')
		if (lastComma === -1) return { label: p, value: 0 }
		return { label: p.slice(0, lastComma).trim(), value: Number(p.slice(lastComma + 1)) || 0 }
	})
	const values = entries.map((e) => e.value)
	const max = Math.max(...values, 1)
	const w = 160
	const h = 44
	const barW = Math.max(4, (w - 24) / entries.length - 4)
	const bars = entries
		.map((e, i) => {
			const x = 12 + i * (barW + 4)
			const barH = max > 0 ? (e.value / max) * (h - 16) : 0
			const y = h - 14 - barH
			return `<rect x="${x}" y="${y}" width="${barW}" height="${barH}" rx="2" fill="var(--chat-bar-fill, #389CEB)" opacity="0.85"/>`
		})
		.join('')
	const labels = entries
		.map((e, i) => {
			const x = 12 + i * (barW + 4) + barW / 2
			const short = e.label.length > 6 ? e.label.slice(0, 5) + '…' : e.label
			return `<text x="${x}" y="${h - 2}" text-anchor="middle" font-size="9" fill="currentColor">${short}</text>`
		})
		.join('')
	return `<svg class="chat-inline-barchart" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">${bars}${labels}</svg>`
}

function injectInlineCharts(html) {
	if (!html || typeof html !== 'string') return html
	let out = html
	const sparklineRe = /\[chart:sparkline:([^\]]+)\]/g
	out = out.replace(sparklineRe, (_, values) => renderSparklineSvg(values.split(',').map((v) => v.trim())))
	const barRe = /\[chart:bar:([^\]]+)\]/g
	out = out.replace(barRe, (_, spec) => renderBarChartSvg(spec))
	return out
}

export default {
	name: 'StatsChatPanel',
	data() {
		return {
			open: false,
			showFab: true,
			hidden: false,
			input: '',
			messages: [],
			loading: false,
			toolStatusText: '',
			copiedIndex: null,
			showChipsInline: false,
			autocompleteItems: [],
			autocompleteIdx: -1,
			roomNames: [],
			abortController: null,
			pinnedChips: [],
			feedbackReasonTags: [
				{ key: 'wrong_data', label: 'chat_feedback_wrong_data' },
				{ key: 'didnt_understand', label: 'chat_feedback_didnt_understand' },
				{ key: 'missing_info', label: 'chat_feedback_missing_info' },
				{ key: 'bad_format', label: 'chat_feedback_bad_format' },
				{ key: 'hallucinated', label: 'chat_feedback_hallucinated' },
			],
		}
	},
	computed: {
		...mapGetters(['filterSelected', 'currentSiteInfo', 'currentFilteringSite', 'isRTL', 'loggedUser', 'isScreenVisible']),
		allChips() {
			return [
				{ icon: '\u{1F534}', label: this.$t('chat_chip_live_status'), prompt: 'What is happening right now? Any open complaints?' },
				{ icon: '\u{1F4CA}', label: this.$t('chat_chip_summary'), prompt: 'Summary' },
				{ icon: '\u{1F6BF}', label: this.$t('chat_chip_cleaning_priority'), prompt: 'Which room should be cleaned next?' },
				{ icon: '\u{1F6A8}', label: this.$t('chat_chip_worst_rooms'), prompt: 'Worst rooms by complaints' },
				{ icon: '\u{1F504}', label: this.$t('chat_chip_compare_periods'), prompt: 'How are we doing compared to the previous period?' },
				{ icon: '\u{23F1}', label: this.$t('chat_chip_sla_issues'), prompt: 'SLA issues' },
				{ icon: '\u{23F0}', label: this.$t('chat_chip_shift_stats'), prompt: 'Which shift performs worst?' },
				{ icon: '\u{1F9F9}', label: this.$t('chat_chip_complaint_types'), prompt: 'What are the main complaint types?' },
				{ icon: '\u{1F551}', label: this.$t('chat_chip_busiest_hours'), prompt: 'Busiest hours' },
				{ icon: '\u{1F4C5}', label: this.$t('chat_chip_worst_days'), prompt: 'Which days of the week have the most complaints?' },
				{ icon: '\u{1F4C8}', label: this.$t('chat_chip_improving'), prompt: 'Which rooms are improving and which are getting worse?' },
				{ icon: '\u{2B50}', label: this.$t('chat_chip_best_rooms'), prompt: 'Best rooms by score' },
				{ icon: '\u{2753}', label: this.$t('chat_chip_help'), prompt: 'What can I ask?' },
			]
		},
		deepChips() {
			return [
				{ icon: '\u{1F4CB}', label: this.$t('chat_chip_briefing'), prompt: 'Give me a morning briefing', deep: true },
				{ icon: '\u{1F3AF}', label: this.$t('chat_chip_focus'), prompt: 'What should I focus on to improve my score?', deep: true },
			]
		},
		welcomeChips() {
			const pinned = this.pinnedChips || []
			const rest = this.allChips.filter((c) => !pinned.some((p) => p.prompt === c.prompt))
			return [...pinned, ...rest]
		},
		inlineChips() {
			return this.allChips
		},
		dynamicChips() {
			const chips = []
			const lastMsg = this._lastAssistantContent()
			if (!lastMsg) return chips
			const lower = lastMsg.toLowerCase()
			if (lower.includes('complaint rate') || lower.includes('worst')) {
				chips.push({ icon: '\u{1F50D}', label: this.$t('chat_chip_why_drilldown'), prompt: 'Why is this room performing badly? Drill down.' })
			}
			if (lower.includes('visit') || lower.includes('complaint')) {
				chips.push({ icon: '\u{1F504}', label: this.$t('chat_chip_compare_periods'), prompt: 'How does this compare to the previous period?' })
			}
			if (lower.includes('shift') || lower.includes('morning') || lower.includes('evening')) {
				chips.push({ icon: '\u{1F551}', label: this.$t('chat_chip_busiest_hours'), prompt: 'Which hours are busiest during that shift?' })
			}
			if (lower.includes('room') && !lower.includes('all rooms')) {
				chips.push({ icon: '\u{1F6BF}', label: this.$t('chat_chip_cleaning_priority'), prompt: 'Which room needs cleaning next?' })
			}
			return chips.slice(0, 3)
		},
		chatPlaceholder() {
			const f = this.filterSelected || {}
			const timeType = f.timeType
			const siteName = this.currentFilteringSite?.name || this.currentSiteInfo?.name || ''
			const periodLabel = timeType ? this.$t(timeType) : ''
			const parts = [periodLabel, siteName].filter(Boolean)
			if (!parts.length) return this.$t('chat_placeholder')
			return this.$t('chat_placeholder_context', { scope: parts.join(' \u00B7 ') })
		},
		userName() {
			const u = this.loggedUser
			if (!u) return ''
			return u.first_name || u.display_name || ''
		},
		headerStatus() {
			if (!this.loading) return this.$t('chat_status_online')
			if (this.toolStatusText) return this.toolStatusText
			return this.$t('chat_status_thinking')
		},
		_persistKey() {
			const f = this.filterSelected || {}
			return getStorageKey(f.company, f.site)
		},
	},
	watch: {
		isScreenVisible(visible) {
			if (visible) {
				this.open = false
				this.showFab = true
			}
		},
		messages: {
			deep: true,
			handler() {
				this.$nextTick(() => this.scrollToBottom())
				this._persistMessages()
			},
		},
		'filterSelected.company'() {
			this.messages = []
			this.input = ''
			this.showChipsInline = false
		},
		'filterSelected.site'() {
			this.messages = []
			this.input = ''
			this.showChipsInline = false
			this._loadRoomNames()
		},
	},
	mounted() {
		this._restoreMessages()
		this._loadRoomNames()
		this._loadPinnedChips()
	},
	methods: {
		openPanel() {
			this.showFab = false
			this.open = true
			trackEvent('chat_open', { event_category: 'chat' })
			this.$nextTick(() => {
				this.scrollToBottom()
				this.$refs.chatInput?.focus()
			})
		},
		onPanelClosed() {
			this.showFab = true
		},
		_lastAssistantContent() {
			for (let i = this.messages.length - 1; i >= 0; i--) {
				if (this.messages[i].role === 'assistant' && this.messages[i].content) {
					return this.messages[i].content
				}
			}
			return ''
		},
		_persistMessages() {
			try {
				const key = this._persistKey
				const data = this.messages.map(m => ({
					role: m.role,
					content: m.content,
					isError: m.isError || false,
				}))
				localStorage.setItem(key, JSON.stringify(data))
			} catch (_) {}
		},
		_restoreMessages() {
			try {
				const key = this._persistKey
				const raw = localStorage.getItem(key)
				if (raw) {
					const parsed = JSON.parse(raw)
					if (Array.isArray(parsed) && parsed.length) {
						this.messages = parsed
					}
				}
			} catch (_) {}
		},
		async _loadRoomNames() {
			this.roomNames = []
			const f = this.filterSelected || {}
			if (!f.company || !f.site) return
			try {
				const ajax = require('../../common/services/httpService').default
				const res = await ajax.get(`room/staticData?siteId=${f.site}`)
				if (res && res.roomOpts) {
					this.roomNames = res.roomOpts.map(r => r.label).filter(Boolean)
				}
			} catch (_) {}
		},
		onInputChange() {
			const text = this.input
			if (!text || text.length < 2 || !this.roomNames.length) {
				this.autocompleteItems = []
				this.autocompleteIdx = -1
				return
			}
			const words = text.split(/\s+/)
			const lastWord = words[words.length - 1].toLowerCase()
			if (lastWord.length < 2) {
				this.autocompleteItems = []
				return
			}
			const matches = this.roomNames.filter(name =>
				name.toLowerCase().includes(lastWord),
			).slice(0, 5)
			this.autocompleteItems = matches
			this.autocompleteIdx = matches.length ? 0 : -1
		},
		selectAutocomplete(dir) {
			if (!this.autocompleteItems.length) return
			this.autocompleteIdx = (this.autocompleteIdx + dir + this.autocompleteItems.length) % this.autocompleteItems.length
		},
		confirmAutocomplete() {
			if (this.autocompleteItems.length && this.autocompleteIdx >= 0) {
				this.applyAutocomplete(this.autocompleteItems[this.autocompleteIdx])
			} else {
				this.send()
			}
		},
		applyAutocomplete(name) {
			const words = this.input.split(/\s+/)
			words[words.length - 1] = name
			this.input = words.join(' ') + ' '
			this.autocompleteItems = []
			this.autocompleteIdx = -1
			this.$refs.chatInput?.focus()
		},
		exportConversation() {
			if (!this.messages.length) return
			const lines = this.messages.map(m => {
				const role = m.role === 'user' ? 'You' : 'Assistant'
				return `${role}:\n${m.content}\n`
			})
			const siteName = this.currentFilteringSite?.name || this.currentSiteInfo?.name || 'site'
			const text = `Chat Export — ${siteName} — ${new Date().toLocaleDateString()}\n${'─'.repeat(50)}\n\n${lines.join('\n')}`
			const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `chat-export-${siteName}-${new Date().toISOString().slice(0, 10)}.txt`
			a.click()
			URL.revokeObjectURL(url)
		},
		toReadableMarkdown(text) {
			const normalized = String(text || '').replace(/\r\n/g, '\n').trim()
			if (!normalized) return ''
			const hasMarkdownSyntax =
				/```|`[^`]+`|^\s{0,3}#{1,6}\s|^\s*[-*+]\s|^\s*\d+\.\s|\[[^\]]+\]\([^)]+\)|^\s*>/m.test(normalized)
			if (hasMarkdownSyntax) return normalized
			const lines = normalized.split('\n').map(line => line.trim()).filter(Boolean)
			if (lines.length > 1) return lines.join('\n\n')
			const sentenceMatches = normalized.match(/[^.!?]+[.!?]+(?:\s|$)|[^.!?]+$/g)
			if (!sentenceMatches || sentenceMatches.length < 3) return normalized
			const sentences = sentenceMatches.map(s => s.trim()).filter(Boolean)
			const chunks = []
			for (let i = 0; i < sentences.length; i += 2) {
				chunks.push(sentences.slice(i, i + 2).join(' '))
			}
			return chunks.join('\n\n')
		},
		renderMarkdown(text) {
			if (!text) return ''
			const rawHtml = markedParser(this.toReadableMarkdown(text))
			const withCharts = injectInlineCharts(rawHtml)
			return DOMPurify.sanitize(withCharts)
		},
		formatTime(ts) {
			if (!ts) return ''
			const d = new Date(ts)
			const h = d.getHours()
			const m = d.getMinutes()
			return `${h}:${String(m).padStart(2, '0')}`
		},
		copyMessage(m) {
			const idx = this.messages.indexOf(m)
			navigator.clipboard.writeText(m.content).then(() => {
				this.copiedIndex = idx
				setTimeout(() => { if (this.copiedIndex === idx) this.copiedIndex = null }, 2000)
			}).catch(() => {})
		},
		shareMessage(m) {
			const siteName = this.currentFilteringSite?.name || this.currentSiteInfo?.name || 'Site'
			const subject = encodeURIComponent(`BuzzzTV Chat — ${siteName}`)
			const body = encodeURIComponent((m.content || '').slice(0, 1500))
			const url = `mailto:?subject=${subject}&body=${body}`
			window.open(url, '_blank')
		},
		_loadPinnedChips() {
			try {
				const raw = localStorage.getItem(STORAGE_KEY_PINNED)
				if (raw) {
					const parsed = JSON.parse(raw)
					if (Array.isArray(parsed)) this.pinnedChips = parsed
				}
			} catch (_) {}
		},
		_savePinnedChips() {
			try {
				localStorage.setItem(STORAGE_KEY_PINNED, JSON.stringify(this.pinnedChips || []))
			} catch (_) {}
		},
		isPinned(chip) {
			return (this.pinnedChips || []).some((p) => p.prompt === chip.prompt)
		},
		togglePin(chip) {
			const pinned = this.pinnedChips || []
			const idx = pinned.findIndex((p) => p.prompt === chip.prompt)
			if (idx >= 0) {
				this.pinnedChips = pinned.filter((_, i) => i !== idx)
			} else {
				this.pinnedChips = [...pinned, { icon: chip.icon, label: chip.label, prompt: chip.prompt }]
			}
			this._savePinnedChips()
		},
		retryFromError(errorIndex) {
			if (this.loading) return
			let userIndex = errorIndex - 1
			while (userIndex >= 0 && this.messages[userIndex].role !== 'user') userIndex--
			if (userIndex < 0) return
			const userText = this.messages[userIndex].content
			this.messages.splice(userIndex, errorIndex - userIndex + 1)
			this.sendWithText(userText)
		},
		send() {
			const text = this.input.trim()
			if (!text || this.loading) return
			this.input = ''
			this.autocompleteItems = []
			this.sendWithText(text)
		},
		sendChip(chip) {
			if (this.loading) return
			this.sendWithText(chip.prompt, chip.label)
		},
		stopGeneration() {
			if (this.abortController) {
				this.abortController.abort()
				this.abortController = null
			}
			this.loading = false
			this.toolStatusText = ''
			const last = this.messages[this.messages.length - 1]
			if (last && last.role === 'assistant' && !last.content) {
				this.messages.pop()
			}
		},
		async sendWithText(text, displayText) {
			if (!text || this.loading) return
			this.messages.push({ role: 'user', content: displayText || text, ts: Date.now() })
			this.messages.push({ role: 'assistant', content: '', logId: null, feedbackGiven: null, showReasonTags: false, feedbackSelectedTags: [], ts: Date.now() })
			this.loading = true
			this.toolStatusText = ''
			trackEvent('chat_message_sent', { event_category: 'chat', event_label: (displayText || text).slice(0, 100) })
			const assistantIndex = this.messages.length - 1
			const controller = new AbortController()
			this.abortController = controller
			const f = this.filterSelected || {}
			const time = f.time
			const context = {
				company: f.company ?? null,
				site: f.site ?? null,
				time: time?.timeFrom && time?.timeTo ? { timeFrom: time.timeFrom, timeTo: time.timeTo } : null,
				timeType: f.timeType ?? null,
				viewTimeBy: f.viewTimeBy ?? null,
				rooms: f.rooms ?? null,
				shift: f.shift ?? null,
				days: f.days ?? null,
				building: f.building ?? null,
				floor: f.floor ?? null,
				gender: f.gender ?? null,
				servikName: f.servikName ?? null,
				language: localeData.language ?? null,
			}
			const history = this.messages.slice(0, -2).map(m => ({ role: m.role, content: m.content }))
			await sendChatMessageStream(text, history, context, {
				signal: controller.signal,
				onContent: (chunk) => {
					this.toolStatusText = ''
					this.messages[assistantIndex].content += chunk
				},
				onToolStatus: (tool) => {
					const key = TOOL_STATUS_LABELS[tool]
					this.toolStatusText = key ? this.$t(key) : ''
				},
				onDone: (logId) => {
					this.$set(this.messages[assistantIndex], 'logId', logId || null)
					this.$set(this.messages[assistantIndex], 'ts', Date.now())
					this.abortController = null
					this.loading = false
					this.toolStatusText = ''
				},
				onError: (message, logId) => {
					if (controller.signal.aborted) return
					this.$set(this.messages, assistantIndex, {
						role: 'assistant',
						content: message || this.$t('chat_error'),
						isError: true,
						logId: logId || null,
						feedbackGiven: null,
						showReasonTags: false,
						feedbackSelectedTags: [],
						ts: Date.now(),
					})
					this.abortController = null
					this.loading = false
					this.toolStatusText = ''
				}
			})
		},
		async giveFeedback(msgIndex, rating) {
			const m = this.messages[msgIndex]
			if (!m || !m.logId || m.feedbackGiven) return
			if (rating === 1) {
				this.$set(this.messages[msgIndex], 'showReasonTags', true)
				this.$set(this.messages[msgIndex], 'feedbackSelectedTags', [])
				this.$set(this.messages[msgIndex], 'feedbackGiven', 'pending')
			} else {
				this.$set(this.messages[msgIndex], 'feedbackGiven', 'thanks')
				trackEvent('chat_feedback', { event_category: 'chat', event_label: 'thumbs_up' })
				try {
					await sendChatFeedback(m.logId, rating)
				} catch (_) {}
				setTimeout(() => {
					if (this.messages[msgIndex]?.feedbackGiven === 'thanks') {
						this.$set(this.messages[msgIndex], 'feedbackGiven', 'done')
					}
				}, 2000)
			}
		},
		toggleFeedbackTag(msgIndex, tag) {
			const m = this.messages[msgIndex]
			if (!m) return
			const tags = [...(m.feedbackSelectedTags || [])]
			const idx = tags.indexOf(tag)
			if (idx >= 0) tags.splice(idx, 1)
			else tags.push(tag)
			this.$set(this.messages[msgIndex], 'feedbackSelectedTags', tags)
		},
		async submitFeedbackTags(msgIndex) {
			const m = this.messages[msgIndex]
			if (!m || !m.logId) return
			const tags = m.feedbackSelectedTags || []
			this.$set(this.messages[msgIndex], 'showReasonTags', false)
			this.$set(this.messages[msgIndex], 'feedbackGiven', 'thanks')
			trackEvent('chat_feedback', { event_category: 'chat', event_label: 'thumbs_down', tags: tags.join(',') })
			try {
				await sendChatFeedback(m.logId, 1, tags)
			} catch (_) {}
			setTimeout(() => {
				if (this.messages[msgIndex]?.feedbackGiven === 'thanks') {
					this.$set(this.messages[msgIndex], 'feedbackGiven', 'done')
				}
			}, 2000)
		},
		clearChat() {
			// Stop any in-flight response when clearing
			if (this.abortController) {
				this.abortController.abort()
				this.abortController = null
			}
			this.loading = false
			this.messages = []
			this.input = ''
			this.showChipsInline = false
			try {
				localStorage.removeItem(this._persistKey)
			} catch (_) {}
		},
		scrollToBottom() {
			const el = this.$refs.messagesContainer
			if (el) el.scrollTop = el.scrollHeight
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/vars.scss" as vars;

.stats-chat {
	position: fixed;
	bottom: 20px;
	right: 20px;
	left: auto;
	z-index: 9999;
}

.stats-chat.is-rtl {
	right: auto;
	left: 20px;
}

@media (max-width: 820px) {
	.stats-chat .chat-fab {
		padding: 12px 14px;
		border-radius: 50%;
		min-width: 48px;
		min-height: 48px;
		justify-content: center;
	}

	.stats-chat .chat-fab-label {
		display: none;
	}

	.stats-chat .chat-fab-mini {
		width: 48px;
		height: 48px;
	}
}

/* ── FAB (BuzzzTech primary) ── */
.chat-fab {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 14px 20px;
	background: linear-gradient(135deg, vars.$blue 0%, vars.$wedgewood-blue 100%);
	color: vars.$white;
	border: none;
	border-radius: 28px;
	box-shadow: 0 4px 16px rgba(56, 156, 235, 0.35);
	cursor: pointer;
	font-size: vars.$font-size-small;
	font-weight: vars.$font-weight-semi-bold;
	font-family: vars.$font-family-main;
	transition: box-shadow 0.2s, transform 0.2s;
}
.chat-fab:hover {
	box-shadow: 0 6px 24px rgba(56, 156, 235, 0.45);
	transform: translateY(-1px);
}
.chat-fab-label {
	@media (max-width: 480px) {
		display: none;
	}
}

/* ── Panel (matches portal card style) ── */
.chat-panel {
	width: 440px;
	max-width: calc(100vw - 32px);
	height: min(640px, calc(100vh - 80px));
	background: vars.$white;
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
	border: 1px solid rgba(0, 0, 0, 0.05);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

/* ── Header (BuzzzTech dark blue) ── */
.chat-panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 16px;
	background: linear-gradient(135deg, vars.$black-blue 0%, vars.$dark-blue 100%);
	color: vars.$white;
}
.chat-header-left {
	display: flex;
	align-items: center;
	gap: 10px;
}
.chat-header-avatar {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: vars.$white;
	padding: 4px;
}
.chat-avatar-logo {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
}
.chat-avatar-logo--welcome {
	width: 72px;
	height: 36px;
}
.chat-avatar-logo--msg {
	width: 100%;
	height: 100%;
}
.chat-header-info {
	display: flex;
	flex-direction: column;
	gap: 1px;
}
.chat-panel-title {
	margin: 0;
	font-size: vars.$font-size-regular;
	font-weight: vars.$font-weight-bold;
	font-family: vars.$font-family-main;
	letter-spacing: 0.02em;
	line-height: 1.2;
}
.chat-header-status {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 11px;
	font-weight: 400;
	opacity: 0.8;
	line-height: 1;
}
.chat-status-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #4ade80;
	flex-shrink: 0;
}
.chat-header-status.thinking .chat-status-dot {
	background: #facc15;
	animation: status-pulse 1.2s ease-in-out infinite;
}
@keyframes status-pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.4; }
}
.chat-header-actions {
	display: flex;
	align-items: center;
	gap: 6px;
}
.chat-header-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.12);
	border: 1px solid rgba(255, 255, 255, 0.2);
	color: vars.$white;
	padding: 6px 8px;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.15s;
}
.chat-header-btn:hover {
	background: rgba(255, 255, 255, 0.22);
}
.chat-close {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	color: vars.$white;
	cursor: pointer;
	padding: 6px;
	border-radius: 8px;
	opacity: 0.9;
	transition: opacity 0.15s, background 0.15s;
}
.chat-close:hover {
	opacity: 1;
	background: rgba(255, 255, 255, 0.12);
}

/* ── Body ── */
.chat-panel-body {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	background: vars.$light-blue;
}

/* ── Messages area ── */
.chat-messages {
	flex: 1;
	overflow-y: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* ── Welcome state ── */
.chat-welcome {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 12px 0 4px;
}
.chat-welcome-icon {
	width: 56px;
	height: 56px;
	border-radius: 12px;
	background: rgba(56, 156, 235, 0.08);
	border: 1px solid rgba(56, 156, 235, 0.12);
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 12px;
	color: vars.$blue;
}
.chat-welcome-text {
	color: vars.$boulder;
	font-size: vars.$font-size-small;
	font-family: vars.$font-family-main;
	text-align: center;
	line-height: 1.5;
	margin: 0 0 16px;
	max-width: 300px;
}
.chat-welcome-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
	width: 100%;
}
.chat-welcome-card {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 32px 10px 12px;
	background: vars.$white;
	border: 1px solid vars.$grey-border-color;
	border-radius: 12px;
	cursor: pointer;
	font-family: vars.$font-family-main;
	text-align: start;
	transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	position: relative;
}
.chat-welcome-card-pin {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 22px;
	height: 22px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	color: vars.$boulder;
	cursor: pointer;
	border-radius: 6px;
	opacity: 0.5;
	transition: opacity 0.15s, color 0.15s, background 0.15s;
}
.is-rtl .chat-welcome-card-pin {
	right: auto;
	left: 6px;
}
.chat-welcome-card:hover .chat-welcome-card-pin,
.chat-welcome-card-pin:hover {
	opacity: 1;
}
.chat-welcome-card-pin.pinned {
	opacity: 1;
	color: #f59e0b;
}
.chat-welcome-card-pin:hover {
	background: rgba(0, 0, 0, 0.05);
}
.chat-welcome-card:hover:not(:disabled) {
	border-color: vars.$blue;
	box-shadow: 0 2px 12px rgba(56, 156, 235, 0.12);
	background: vars.$light-blue;
}
.chat-welcome-card:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.chat-welcome-card-icon {
	font-size: 16px;
	flex-shrink: 0;
	line-height: 1;
}
.chat-welcome-card-label {
	font-size: vars.$font-size-smaller;
	color: vars.$black;
	line-height: 1.3;
	font-weight: vars.$font-weight-semi-bold;
	flex: 1;
	min-width: 0;
}
.chat-deep-section {
	margin-top: 12px;
	width: 100%;
}
.chat-deep-label {
	display: flex;
	align-items: center;
	gap: 5px;
	font-size: 11px;
	font-weight: 600;
	color: vars.$grey;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 6px;
	padding-left: 2px;
}
.is-rtl .chat-deep-label {
	padding-left: 0;
	padding-right: 2px;
}
.chat-welcome-card-deep {
	border-style: dashed;
	background: rgba(56, 156, 235, 0.03);
}
.chat-welcome-card-deep:hover:not(:disabled) {
	border-style: solid;
}
.chat-inline-sparkline {
	display: inline-block;
	vertical-align: middle;
	margin: 4px 0;
	color: vars.$blue;
}
.chat-inline-barchart {
	display: block;
	margin: 6px 0;
	color: vars.$black;
}
.chat-msg-markdown ::v-deep .chat-inline-sparkline {
	display: inline-block;
	vertical-align: middle;
}
.chat-msg-markdown ::v-deep .chat-inline-barchart {
	display: block;
}

/* ── Chips toggle (conversation mode) ── */
.chat-chips-toggle {
	padding: 4px 16px 2px;
}
.chat-chips-toggle-btn {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: none;
	border: none;
	color: vars.$blue;
	font-size: vars.$font-size-smaller;
	font-family: vars.$font-family-main;
	font-weight: vars.$font-weight-semi-bold;
	cursor: pointer;
	padding: 4px 0;
	transition: opacity 0.15s;
}
.chat-chips-toggle-btn:hover { opacity: 0.8; }

/* ── Chips row (conversation mode) ── */
.chat-chips-row {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	border-top: 1px solid vars.$grey-border-color;
	background: vars.$white;
}
.chat-chips-hide {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	color: vars.$boulder;
	cursor: pointer;
	padding: 2px;
	flex-shrink: 0;
}
.chat-chips-scroll {
	display: flex;
	gap: 6px;
	overflow-x: auto;
	flex: 1;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar { display: none; }
}
.chat-chip-pill {
	white-space: nowrap;
	padding: 6px 12px;
	border: 1px solid vars.$grey-border-color;
	border-radius: 14px;
	background: vars.$white;
	color: vars.$black;
	font-size: vars.$font-size-tiny;
	font-family: vars.$font-family-main;
	cursor: pointer;
	flex-shrink: 0;
	transition: border-color 0.15s, background 0.15s;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.chat-chip-pill:hover:not(:disabled) {
	background: vars.$light-blue;
	border-color: vars.$blue;
	color: vars.$blue;
}
.chat-chip-pill:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.chat-chip-dynamic {
	border-color: rgba(56, 156, 235, 0.3);
	background: rgba(56, 156, 235, 0.06);
	color: vars.$wedgewood-blue;
}
.chat-chip-dynamic:hover:not(:disabled) {
	background: rgba(56, 156, 235, 0.1);
	border-color: vars.$blue;
	color: vars.$blue;
}

/* ── Messages ── */
.chat-msg {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 8px;
	max-width: 88%;
	align-self: flex-start;
	animation: chat-msg-in 0.2s ease;
}
.chat-msg-avatar {
	flex-shrink: 0;
	margin-top: 2px;
	line-height: 0;
	width: 24px;
	height: 24px;
	border-radius: 6px;
	background: vars.$white;
	padding: 2px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.chat-msg-body {
	display: flex;
	flex-direction: column;
	min-width: 0;
}
.chat-msg-time {
	font-size: 10px;
	color: vars.$grey;
	margin-top: 3px;
	padding-inline-start: 6px;
	opacity: 0;
	transition: opacity 0.15s;
}
.chat-msg:hover .chat-msg-time {
	opacity: 1;
}
@keyframes chat-msg-in {
	from { opacity: 0; transform: translateY(6px); }
	to { opacity: 1; transform: translateY(0); }
}
.chat-msg.user {
	align-self: flex-end;
}
.chat-msg-content {
	padding: 10px 14px;
	border-radius: 12px;
	font-size: vars.$font-size-small;
	font-family: vars.$font-family-main;
	line-height: 1.55;
	white-space: pre-wrap;
	word-break: break-word;
	text-align: start;
}
.chat-msg.user .chat-msg-content {
	background: linear-gradient(135deg, vars.$blue 0%, vars.$wedgewood-blue 100%);
	color: vars.$white;
	border-bottom-right-radius: 4px;
}
.chat-msg.assistant .chat-msg-content {
	background: vars.$white;
	color: vars.$black;
	border-bottom-left-radius: 4px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
	border: 1px solid rgba(0, 0, 0, 0.04);
}

[dir="rtl"] .chat-msg.user .chat-msg-content {
	border-bottom-right-radius: 14px;
	border-bottom-left-radius: 4px;
}
[dir="rtl"] .chat-msg.assistant .chat-msg-content {
	border-bottom-left-radius: 14px;
	border-bottom-right-radius: 4px;
}

/* ── Markdown ── */
.chat-msg-markdown {
	white-space: normal;
	letter-spacing: 0.01em;
}
.chat-msg-markdown ::v-deep {
	p { margin: 0 0 0.6em; &:last-child { margin-bottom: 0; } }
	ul, ol { margin: 0.4em 0 0.6em; padding-inline-start: 1.25em; }
	li { margin-bottom: 0.2em; }
	h1, h2, h3, h4 { margin: 0.7em 0 0.35em; font-size: 1.03em; font-weight: 700; line-height: 1.35; }
	h1 { font-size: 1.12em; }
	h2 { font-size: 1.08em; }
	strong { font-weight: 700; }
	code { background: rgba(0,0,0,0.06); padding: 2px 5px; border-radius: 4px; font-size: 0.9em; }
	pre { background: rgba(0,0,0,0.05); padding: 8px 10px; border-radius: 8px; overflow-x: auto; margin: 0.5em 0; }
	pre code { background: none; padding: 0; }
	table { border-collapse: collapse; margin: 0.5em 0; font-size: 0.9em; width: 100%; }
	th, td { border: 1px solid rgba(0,0,0,0.1); padding: 4px 8px; text-align: start; }
	th { background: rgba(0,0,0,0.03); font-weight: 600; }
}

/* ── Error ── */
.chat-msg-error .chat-msg-content {
	background: vars.$light-red;
	color: vars.$red;
	box-shadow: none;
	border-color: rgba(255, 76, 102, 0.2);
}

/* ── Actions (copy/retry/share) ── */
.chat-msg-actions {
	display: flex;
	gap: 2px;
	margin-top: 3px;
	padding-inline-start: 6px;
}
.chat-action-btn {
	display: inline-flex;
	align-items: center;
	gap: 3px;
	background: none;
	border: none;
	color: vars.$boulder;
	font-size: vars.$font-size-tiny;
	font-family: vars.$font-family-main;
	cursor: pointer;
	padding: 2px 5px;
	border-radius: 6px;
	opacity: 0;
	transition: opacity 0.15s, background 0.15s;
}
.chat-msg:hover .chat-action-btn {
	opacity: 1;
}
.chat-action-btn:hover {
	background: rgba(0,0,0,0.05);
	color: vars.$black;
}
.chat-retry-btn {
	opacity: 1 !important;
	color: vars.$red;
}
.chat-retry-btn:hover {
	background: vars.$light-red;
	color: vars.$red;
}
.chat-share-btn span {
	@media (max-width: 380px) {
		display: none;
	}
}

/* ── Loading ── */
.chat-loading {
	display: flex;
	align-items: center;
	min-height: 28px;
	background: vars.$white !important;
	box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
}
.loading-dots {
	display: inline-flex;
	gap: 5px;
	align-items: center;
}
.loading-dots span {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: vars.$blue;
	animation: chat-dot-bounce 1.4s ease-in-out infinite;
}
.loading-dots span:nth-child(2) { animation-delay: 0.16s; }
.loading-dots span:nth-child(3) { animation-delay: 0.32s; }
@keyframes chat-dot-bounce {
	0%, 80%, 100% { opacity: 0.25; transform: scale(0.7); }
	40% { opacity: 1; transform: scale(1); }
}

/* ── Tool status label ── */
.chat-loading {
	display: flex;
	align-items: center;
	gap: 10px;
}
.chat-tool-status {
	font-size: 12px;
	color: vars.$grey;
	font-style: italic;
	animation: tool-status-in 0.25s ease;
}
@keyframes tool-status-in {
	from { opacity: 0; transform: translateX(-4px); }
	to { opacity: 1; transform: translateX(0); }
}

/* ── Welcome greeting ── */
.chat-welcome-greeting {
	margin: 0 0 2px;
	font-size: 15px;
	font-weight: vars.$font-weight-bold;
	color: vars.$black;
	font-family: vars.$font-family-main;
}

/* ── Input form ── */
.chat-form {
	display: flex;
	gap: 8px;
	padding: 10px 16px 14px;
	background: vars.$white;
	border-top: 1px solid vars.$grey-border-color;
}
.chat-input-wrapper {
	flex: 1;
	position: relative;
}
.chat-input {
	width: 100%;
	padding: 10px 14px;
	border: 1px solid vars.$grey-border-color;
	border-radius: 12px;
	font-size: vars.$font-size-small;
	font-family: vars.$font-family-main;
	background: vars.$white;
	transition: border-color 0.15s, background 0.15s;
	box-sizing: border-box;
}
.chat-input:focus {
	outline: none;
	border-color: vars.$blue;
	box-shadow: 0 0 0 2px rgba(56, 156, 235, 0.15);
}
.chat-autocomplete {
	position: absolute;
	bottom: 100%;
	left: 0;
	right: 0;
	background: vars.$white;
	border: 1px solid vars.$grey-border-color;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	margin-bottom: 4px;
	max-height: 160px;
	overflow-y: auto;
	z-index: 10;
}
.chat-autocomplete-item {
	display: block;
	width: 100%;
	padding: 8px 12px;
	background: none;
	border: none;
	text-align: start;
	font-size: vars.$font-size-small;
	font-family: vars.$font-family-main;
	color: vars.$black;
	cursor: pointer;
	transition: background 0.1s;
}
.chat-autocomplete-item:hover,
.chat-autocomplete-item.active {
	background: vars.$light-blue;
	color: vars.$blue;
}
.chat-send,
.chat-stop {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border: none;
	border-radius: 12px;
	cursor: pointer;
	flex-shrink: 0;
	transition: opacity 0.15s, transform 0.15s;
}
.chat-send {
	background: linear-gradient(135deg, vars.$blue 0%, vars.$wedgewood-blue 100%);
	color: vars.$white;
}
.chat-send:hover:not(:disabled) {
	transform: scale(1.04);
}
.chat-send:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}
.chat-stop {
	background: vars.$light-red;
	color: vars.$red;
	border: 1.5px solid rgba(255, 76, 102, 0.4);
	animation: stop-pulse 2s ease-in-out infinite;
}
.chat-stop:hover {
	background: vars.$light-red;
	border-color: vars.$red;
	transform: scale(1.04);
}
@keyframes stop-pulse {
	0%, 100% { box-shadow: 0 0 0 0 rgba(255, 76, 102, 0.15); }
	50% { box-shadow: 0 0 0 4px rgba(255, 76, 102, 0.1); }
}

/* ── FAB wrap (for hide button) ── */
.chat-fab-wrap {
	position: relative;
	display: inline-flex;
}
.chat-fab-hide {
	position: absolute;
	top: -6px;
	right: -6px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: vars.$white;
	border: 1px solid vars.$grey-border-color;
	color: vars.$boulder;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	opacity: 0;
	transition: opacity 0.15s, background 0.15s;
	box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}
.is-rtl .chat-fab-hide {
	right: auto;
	left: -6px;
}
.chat-fab-wrap:hover .chat-fab-hide {
	opacity: 1;
}
.chat-fab-hide:hover {
	background: vars.$light-red;
	color: vars.$red;
	border-color: rgba(255, 76, 102, 0.4);
}

/* ── Mini FAB (hidden state) ── */
.chat-fab-mini {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: vars.$light-blue;
	color: vars.$blue;
	border: 1px solid rgba(56, 156, 235, 0.25);
	cursor: pointer;
	transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.chat-fab-mini:hover {
	background: rgba(56, 156, 235, 0.15);
	transform: scale(1.06);
	box-shadow: 0 4px 12px rgba(56, 156, 235, 0.2);
}

/* ── FAB fade transition ── */
.fab-fade-enter-active {
	transition: opacity 0.2s ease 0.05s, transform 0.2s ease 0.05s;
}
.fab-fade-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.fab-fade-enter,
.fab-fade-leave-to {
	opacity: 0;
	transform: scale(0.8);
}

/* ── Panel transition ── */
.chat-panel-enter-active {
	transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.chat-panel-leave-active {
	transition: opacity 0.2s cubic-bezier(0.4, 0, 1, 1), transform 0.2s cubic-bezier(0.4, 0, 1, 1);
}
.chat-panel {
	transform-origin: bottom right;
}
.is-rtl .chat-panel {
	transform-origin: bottom left;
}
.chat-panel-enter,
.chat-panel-leave-to {
	opacity: 0;
	transform: translateY(12px) scale(0.95);
}

/* ── Feedback (thumbs up/down) ── */
.chat-feedback-divider {
	width: 1px;
	height: 14px;
	background: vars.$grey-border-color;
	margin: 0 2px;
	flex-shrink: 0;
}
.chat-feedback-btn {
	opacity: 0;
	transition: opacity 0.15s, background 0.15s, color 0.15s;
}
.chat-msg:hover .chat-feedback-btn {
	opacity: 1;
}
.chat-feedback-up:hover {
	color: #22c55e;
	background: rgba(34, 197, 94, 0.08);
}
.chat-feedback-down:hover {
	color: vars.$red;
	background: rgba(255, 76, 102, 0.08);
}
.chat-feedback-thanks {
	font-size: vars.$font-size-tiny;
	color: #22c55e;
	font-weight: vars.$font-weight-semi-bold;
	padding: 2px 5px;
	animation: chat-msg-in 0.2s ease;
}
.chat-feedback-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 4px;
	padding-inline-start: 6px;
	animation: chat-msg-in 0.2s ease;
}
.chat-feedback-tag {
	padding: 4px 10px;
	border: 1px solid vars.$grey-border-color;
	border-radius: 12px;
	background: vars.$white;
	color: vars.$boulder;
	font-size: vars.$font-size-tiny;
	font-family: vars.$font-family-main;
	cursor: pointer;
	transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.chat-feedback-tag:hover {
	border-color: vars.$blue;
	color: vars.$blue;
}
.chat-feedback-tag.selected {
	background: rgba(56, 156, 235, 0.1);
	border-color: vars.$blue;
	color: vars.$blue;
	font-weight: vars.$font-weight-semi-bold;
}
.chat-feedback-tag-submit {
	background: vars.$blue;
	color: vars.$white;
	border-color: vars.$blue;
	font-weight: vars.$font-weight-semi-bold;
}
.chat-feedback-tag-submit:hover {
	background: vars.$wedgewood-blue;
	border-color: vars.$wedgewood-blue;
	color: vars.$white;
}
</style>
